/**
 * Export content to Word (.docx) format.
 *
 * Converts markdown-like content into a structured Word document
 * using the `docx` library.
 */

import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  BorderStyle,
  ImageRun,
  LevelFormat,
  PageOrientation,
  Table,
  TableRow,
  TableCell,
  WidthType,
  ShadingType,
  type ISectionOptions,
} from "docx";
import { AI_DISCLAIMER } from "@cvx/lib/aiDisclaimer";
import { renderChartsForExport, type ExportChart } from "./exportCharts";

/** Narrow a dynamic value to a plain object without discarding type safety. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

/** Parse inline bold/italic formatting */
function parseInlineFormatting(text: string): TextRun[] {
  const runs: TextRun[] = [];
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*|([^*]+))/g;
  let match: RegExpExecArray | null;

  while ((match = regex.exec(text)) !== null) {
    if (match[2]) {
      runs.push(new TextRun({ text: match[2], bold: true }));
    } else if (match[3]) {
      runs.push(new TextRun({ text: match[3], italics: true }));
    } else if (match[4]) {
      runs.push(new TextRun({ text: match[4] }));
    }
  }

  if (runs.length === 0) {
    runs.push(new TextRun({ text }));
  }

  return runs;
}

/** Build a docx Table from parsed row data */
function buildTable(rows: string[][]): (Paragraph | Table)[] {
  if (rows.length === 0) return [];

  const maxCols = Math.max(...rows.map((r) => r.length));
  const tableRows = rows.map(
    (row, rowIdx) =>
      new TableRow({
        children: Array.from(
          { length: maxCols },
          (_, colIdx) =>
            new TableCell({
              children: [
                new Paragraph({
                  children: [
                    new TextRun({
                      text: row[colIdx] ?? "",
                      bold: rowIdx === 0,
                      size: 20,
                    }),
                  ],
                }),
              ],
              shading:
                rowIdx === 0
                  ? { type: ShadingType.SOLID, color: "F0F0F0" }
                  : undefined,
              width: {
                size: Math.floor(100 / maxCols),
                type: WidthType.PERCENTAGE,
              },
            }),
        ),
      }),
  );

  return [
    new Paragraph({ spacing: { before: 120 } }),
    new Table({
      rows: tableRows,
      width: { size: 100, type: WidthType.PERCENTAGE },
    }),
    new Paragraph({ spacing: { after: 120 } }),
  ];
}

/** Simple markdown-to-docx paragraph parser */
function parseMarkdownToParagraphs(markdown: string): (Paragraph | Table)[] {
  const lines = markdown.split("\n");
  const elements: (Paragraph | Table)[] = [];
  let inTable = false;
  let tableRows: string[][] = [];

  for (const line of lines) {
    const trimmed = line.trim();

    if (!trimmed) {
      if (inTable && tableRows.length > 0) {
        elements.push(...buildTable(tableRows));
        tableRows = [];
        inTable = false;
      }
      continue;
    }

    // Table rows
    if (trimmed.startsWith("|") && trimmed.endsWith("|")) {
      if (/^\|[\s\-:|]+\|$/.test(trimmed)) {
        inTable = true;
        continue;
      }
      inTable = true;
      const cells = trimmed
        .slice(1, -1)
        .split("|")
        .map((c) => c.trim());
      tableRows.push(cells);
      continue;
    }

    // Flush any pending table
    if (inTable && tableRows.length > 0) {
      elements.push(...buildTable(tableRows));
      tableRows = [];
      inTable = false;
    }

    // Headings
    if (trimmed.startsWith("#### ")) {
      elements.push(
        new Paragraph({
          text: trimmed.slice(5),
          heading: HeadingLevel.HEADING_4,
          spacing: { before: 240, after: 120 },
        }),
      );
    } else if (trimmed.startsWith("### ")) {
      elements.push(
        new Paragraph({
          text: trimmed.slice(4),
          heading: HeadingLevel.HEADING_3,
          spacing: { before: 320, after: 120 },
        }),
      );
    } else if (trimmed.startsWith("## ")) {
      elements.push(
        new Paragraph({
          text: trimmed.slice(3),
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 160 },
        }),
      );
    } else if (trimmed.startsWith("# ")) {
      elements.push(
        new Paragraph({
          text: trimmed.slice(2),
          heading: HeadingLevel.HEADING_1,
          spacing: { before: 480, after: 200 },
        }),
      );
    }
    // Bullet points
    else if (trimmed.startsWith("- ") || trimmed.startsWith("* ")) {
      elements.push(
        new Paragraph({
          children: parseInlineFormatting(trimmed.slice(2)),
          bullet: { level: 0 },
          spacing: { after: 60 },
        }),
      );
    }
    // Numbered lists
    else if (/^\d+\.\s/.test(trimmed)) {
      const text = trimmed.replace(/^\d+\.\s/, "");
      elements.push(
        new Paragraph({
          children: parseInlineFormatting(text),
          numbering: { reference: "default-numbering", level: 0 },
          spacing: { after: 60 },
        }),
      );
    }
    // Blockquotes
    else if (trimmed.startsWith("> ")) {
      elements.push(
        new Paragraph({
          children: parseInlineFormatting(trimmed.slice(2)),
          indent: { left: 720 },
          border: {
            left: { style: BorderStyle.SINGLE, size: 6, color: "999999" },
          },
          spacing: { after: 120 },
        }),
      );
    }
    // Horizontal rule
    else if (/^[-*_]{3,}$/.test(trimmed)) {
      elements.push(
        new Paragraph({
          border: {
            bottom: { style: BorderStyle.SINGLE, size: 1, color: "CCCCCC" },
          },
          spacing: { before: 200, after: 200 },
        }),
      );
    }
    // Regular paragraph
    else {
      elements.push(
        new Paragraph({
          children: parseInlineFormatting(trimmed),
          spacing: { after: 120 },
        }),
      );
    }
  }

  // Flush remaining table
  if (tableRows.length > 0) {
    elements.push(...buildTable(tableRows));
  }

  return elements;
}

/** Convert structured JSON output to markdown for export */
export function outputToMarkdown(
  output: Record<string, unknown>,
  title: string,
): string {
  const lines: string[] = [`# ${title}`, ""];

  function renderValue(value: unknown, depth: number): void {
    if (value === null || value === undefined) return;

    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      lines.push(String(value));
      return;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        if (typeof item === "string") {
          lines.push(`- ${item}`);
        } else if (isRecord(item)) {
          renderObject(item, depth);
          lines.push("");
        }
      }
      return;
    }

    if (isRecord(value)) {
      renderObject(value, depth);
    }
  }

  function renderObject(obj: Record<string, unknown>, depth: number): void {
    for (const [key, val] of Object.entries(obj)) {
      const label = key
        .replace(/([A-Z])/g, " $1")
        .replace(/_/g, " ")
        .replace(/^\w/, (c) => c.toUpperCase())
        .trim();

      if (val === null || val === undefined) continue;

      if (typeof val === "object" && !Array.isArray(val)) {
        const prefix = "#".repeat(Math.min(depth + 2, 4));
        lines.push(`${prefix} ${label}`);
        lines.push("");
        renderValue(val, depth + 1);
        lines.push("");
      } else if (Array.isArray(val)) {
        const prefix = "#".repeat(Math.min(depth + 2, 4));
        lines.push(`${prefix} ${label}`);
        lines.push("");
        renderValue(val, depth + 1);
        lines.push("");
      } else {
        lines.push(`**${label}:** ${String(val)}`);
      }
    }
  }

  renderObject(output, 0);
  return lines.join("\n");
}

/** Convert chat messages to markdown */
export function chatToMarkdown(
  messages: Array<{ role: string; content: string }>,
  title: string,
): string {
  const lines: string[] = [`# ${title}`, ""];

  for (const msg of messages) {
    const speaker = msg.role === "user" ? "User" : "Morgan AI";
    lines.push(`## ${speaker}`);
    lines.push("");
    lines.push(msg.content);
    lines.push("");
    lines.push("---");
    lines.push("");
  }

  return lines.join("\n");
}

export interface ExportSheet {
  name: string;
  headers: string[];
  rows: (string | number | Date | null | undefined)[][];
  /** Skip this sheet in Excel when it is only useful as a Word/PDF summary. */
  omitFromExcel?: boolean;
  /** Skip this sheet in Word/PDF when it is only useful as an Excel tab. */
  omitFromWordPdf?: boolean;
  /**
   * Charts drawn after this sheet's table in Word/PDF (MOR-978). Ignored by
   * Excel, where practitioners chart from the raw series natively.
   */
  charts?: ExportChart[];
  /**
   * Row indexes (into `rows`) that are section labels rather than data —
   * rendered bold with shading so the grouped worksheet layout survives
   * the trip into a flat table.
   */
  sectionRows?: number[];
}

/**
 * Format a sheet cell for Word/PDF display: real numbers become en-AU
 * formatted strings (whole numbers without decimals, otherwise 2dp) so
 * exported figures read like the on-screen worksheet, not float dumps.
 */
export function formatSheetCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) return value.toLocaleDateString("en-AU");
  if (typeof value === "number") {
    return Number.isInteger(value)
      ? value.toLocaleString("en-AU")
      : value.toLocaleString("en-AU", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
  }
  return String(value);
}

/**
 * Proportional column widths (in %) from content length: the label column
 * gets the room its text needs instead of an even split that wraps every
 * word — the "fonts out of shape" complaint in MOR-978.
 */
export function sheetColumnPercents(sheet: ExportSheet): number[] {
  const colCount = sheet.headers.length;
  if (colCount === 0) return [];
  const naturals = sheet.headers.map((h, i) => {
    const max = Math.max(
      h.length,
      ...sheet.rows.map((row) => formatSheetCell(row[i]).length),
    );
    // Floor keeps narrow numeric columns readable; cap stops a narrative
    // column from starving the rest.
    return Math.min(Math.max(max, 6), 40);
  });
  const total = naturals.reduce((a, b) => a + b, 0);
  return naturals.map((n) => (n / total) * 100);
}

export interface ExportContent {
  title: string;
  markdown?: string;
  output?: Record<string, unknown>;
  messages?: Array<{ role: string; content: string }>;
  sheets?: ExportSheet[];
}

/** Wide registers flip to landscape, matching the PDF exporter's threshold. */
const WORD_LANDSCAPE_COL_THRESHOLD = 6;

/**
 * A4 page size in twips, for the landscape section flip. Portrait sections
 * use docx's A4 default and the PDF exporter is A4, so landscape must match
 * rather than silently switching to US Letter.
 */
const PAGE_W_TWIPS = 11906;
const PAGE_H_TWIPS = 16838;

/** Usable content width in px (96dpi) inside 1in margins, to size images. */
const PORTRAIT_CONTENT_PX = 624;
const LANDSCAPE_CONTENT_PX = 864;

/** Build a docx Table for a structured sheet: proportional column widths,
 * right-aligned numerics, shaded header and section-label rows. */
function buildSheetTable(sheet: ExportSheet): (Paragraph | Table)[] {
  const colCount = sheet.headers.length;
  if (colCount === 0) return [];
  const widths = sheetColumnPercents(sheet);
  const sectionRows = new Set(sheet.sectionRows ?? []);

  const makeRow = (
    cells: (string | number | Date | null | undefined)[],
    opts: { header?: boolean; section?: boolean },
  ) =>
    new TableRow({
      children: Array.from({ length: colCount }, (_, colIdx) => {
        const raw = cells[colIdx];
        return new TableCell({
          children: [
            new Paragraph({
              alignment:
                typeof raw === "number" ? AlignmentType.RIGHT : undefined,
              children: [
                new TextRun({
                  text: formatSheetCell(raw),
                  bold: opts.header || opts.section,
                  size: 18,
                }),
              ],
            }),
          ],
          shading: opts.header
            ? { type: ShadingType.SOLID, color: "F0F0F0" }
            : opts.section
              ? { type: ShadingType.SOLID, color: "F7F7F7" }
              : undefined,
          width: {
            size: widths[colIdx],
            type: WidthType.PERCENTAGE,
          },
        });
      }),
    });

  return [
    new Paragraph({ spacing: { before: 120 } }),
    new Table({
      rows: [
        makeRow(sheet.headers, { header: true }),
        ...sheet.rows.map((row, i) =>
          makeRow(row, { section: sectionRows.has(i) }),
        ),
      ],
      width: { size: 100, type: WidthType.PERCENTAGE },
    }),
    new Paragraph({ spacing: { after: 120 } }),
  ];
}

/**
 * Render structured sheets straight to docx elements — tables plus their
 * charts (MOR-978) — instead of round-tripping through markdown, which
 * could not carry images.
 */
async function sheetsToDocxElements(
  title: string,
  sheets: ExportSheet[],
  contentPx: number,
): Promise<(Paragraph | Table)[]> {
  const elements: (Paragraph | Table)[] = [
    new Paragraph({
      text: title,
      heading: HeadingLevel.HEADING_1,
      spacing: { before: 240, after: 200 },
    }),
  ];

  for (const sheet of sheets) {
    if (sheet.omitFromWordPdf) continue;
    // Skip a sheet heading that just repeats the document title.
    if (sheet.name.trim().toLowerCase() !== title.trim().toLowerCase()) {
      elements.push(
        new Paragraph({
          text: sheet.name,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 400, after: 160 },
        }),
      );
    }
    elements.push(...buildSheetTable(sheet));

    for (const { chart, image } of await renderChartsForExport(sheet.charts)) {
      const width = Math.min(image.width, contentPx);
      const height = Math.round((width / image.width) * image.height);
      elements.push(
        new Paragraph({
          children: [new TextRun({ text: chart.title, bold: true, size: 20 })],
          spacing: { before: 240, after: 80 },
        }),
        new Paragraph({
          children: [
            new ImageRun({
              type: "png",
              data: image.bytes,
              transformation: { width, height },
            }),
          ],
          spacing: { after: 160 },
        }),
      );
    }
  }

  return elements;
}

export async function exportToWord(content: ExportContent): Promise<Blob> {
  const sheets = content.sheets ?? [];
  const maxCols =
    sheets.length > 0 ? Math.max(...sheets.map((s) => s.headers.length)) : 0;
  const landscape = maxCols >= WORD_LANDSCAPE_COL_THRESHOLD;

  const body: (Paragraph | Table)[] = content.messages
    ? parseMarkdownToParagraphs(chatToMarkdown(content.messages, content.title))
    : content.markdown
      ? parseMarkdownToParagraphs(content.markdown)
      : sheets.length > 0
        ? await sheetsToDocxElements(
            content.title,
            sheets,
            landscape ? LANDSCAPE_CONTENT_PX : PORTRAIT_CONTENT_PX,
          )
        : content.output
          ? parseMarkdownToParagraphs(
              outputToMarkdown(content.output, content.title),
            )
          : parseMarkdownToParagraphs(
              `# ${content.title}\n\nNo content available.`,
            );

  // One explicit font family throughout — documents otherwise mix Word's
  // theme fonts (blue Calibri Light headings, themed body) with our sized
  // runs, which read as distorted next to the on-screen worksheet (MOR-978).
  const sectionProperties: ISectionOptions["properties"] = {
    page: {
      margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
      ...(landscape
        ? {
            size: {
              width: PAGE_H_TWIPS,
              height: PAGE_W_TWIPS,
              orientation: PageOrientation.LANDSCAPE,
            },
          }
        : {}),
    },
  };

  const doc = new Document({
    styles: {
      default: {
        document: { run: { font: "Calibri", size: 22 } },
        // Override docx's built-in Heading1..4 defaults through the dedicated
        // `default.headingN` slots. Declaring them as `paragraphStyles` with the
        // same ids emits a second <w:style w:styleId="Heading1"> in styles.xml,
        // which Word resolves to the built-in one and ignores ours (MOR-978).
        ...Object.fromEntries(
          (
            [
              ["heading1", 32, 480, 200],
              ["heading2", 27, 400, 160],
              ["heading3", 24, 320, 120],
              ["heading4", 22, 240, 120],
            ] as const
          ).map(([slot, size, before, after]) => [
            slot,
            {
              run: { font: "Calibri", size, bold: true, color: "1F2937" },
              paragraph: { spacing: { before, after } },
            },
          ]),
        ),
      },
    },
    numbering: {
      config: [
        {
          reference: "default-numbering",
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: "%1.",
              alignment: AlignmentType.LEFT,
            },
          ],
        },
      ],
    },
    sections: [
      {
        properties: sectionProperties,
        children: [
          new Paragraph({
            alignment: AlignmentType.RIGHT,
            children: [
              new TextRun({
                text: `Exported ${new Date().toLocaleDateString()}`,
                size: 18,
                color: "999999",
                italics: true,
              }),
            ],
            spacing: { after: 400 },
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: AI_DISCLAIMER,
                size: 16,
                color: "777777",
                italics: true,
              }),
            ],
            spacing: { after: 400 },
          }),
          ...body,
        ],
      },
    ],
  });

  return Packer.toBlob(doc);
}
